import type { FrontModule } from "@nabble/sdk";
import { type ReactNode, Suspense } from "react";
import { Navigate, Route, type RouteObject } from "react-router-dom";
import { moduleLandingRedirects, modules } from "../modules";

type ModuleRoute = FrontModule["routes"][number];

/** What a module's route renders: its element, or its component. */
export function routeElement(route: ModuleRoute | RouteObject): ReactNode {
  const Component = route.Component;
  return route.element ?? (Component ? <Component /> : null);
}

/** A public route's path under `base` ("/p/<module-id>/" in the suite, "/" on the main domain). */
export function publicRoutePath(base: string, route: RouteObject): string {
  return `${base}${String(route.path ?? "").replace(/^\/+/, "")}`;
}

/** Modules' public pages (`/p/<module-id>/…`, docs/adr/0015): no session, no AppShell. */
export function publicModuleRoutes(fallback: ReactNode): React.JSX.Element[] {
  return modules.flatMap((mod) =>
    (mod.publicRoutes ?? []).map((route) => {
      const path = publicRoutePath(`/p/${mod.id}/`, route);
      return <Route key={path} path={path} element={<Suspense fallback={fallback}>{routeElement(route)}</Suspense>} />;
    }),
  );
}

/** Each module's pages inside the shell, plus the redirects from a module's root to its first page. */
export function signedInModuleRoutes(): React.JSX.Element[] {
  return [
    ...moduleLandingRedirects().map(([from, to]) => (
      <Route key={`landing-${from}`} path={from} element={<Navigate to={to} replace />} />
    )),
    ...modules.flatMap((mod) =>
      mod.routes.map((route) => <Route key={String(route.path)} path={route.path} element={routeElement(route)} />),
    ),
  ];
}
